import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Player, PlayerRemovalRequest } from '@/lib/types';
import { toast } from './use-toast';

export function usePlayerDeletion() {
  const queryClient = useQueryClient();

  // Get deleted players
  const { data: deletedPlayers = [], isLoading: isLoadingDeleted } = useQuery<Player[]>({
    queryKey: ['/api/players/deleted'],
    staleTime: 5 * 60 * 1000,
  });

  // Remove player mutation
  const removePlayerMutation = useMutation({
    mutationFn: async ({ playerId, reason }: { playerId: number; reason: PlayerRemovalRequest['reason'] }) => {
      const response = await apiRequest('DELETE', `/api/players/${playerId}/remove`, { reason });
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Failed to remove player');
      }
      return response.json();
    },
    onSuccess: (_, { playerId }) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/players/'] });
      queryClient.invalidateQueries({ queryKey: ['/api/players/deleted'] });
      
      // Update the players list cache to remove the deleted player
      queryClient.setQueryData(['/api/players/'], (oldData: Player[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter(player => player.id !== playerId);
      });
      
      toast({
        title: 'Player removed',
        description: 'The player has been removed from your database.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove player',
        variant: 'destructive',
      });
    },
  });

  // Remove multiple players mutation
  const removeManyPlayersMutation = useMutation({
    mutationFn: async ({ playerIds, reason }: { playerIds: number[]; reason: PlayerRemovalRequest['reason'] }) => {
      const promises = playerIds.map(playerId =>
        apiRequest('DELETE', `/api/players/${playerId}/remove`, { reason })
      );
      
      const responses = await Promise.all(promises);
      
      // Check if all responses are ok
      const failedResponses = responses.filter(r => !r.ok);
      if (failedResponses.length > 0) {
        throw new Error(`Failed to remove ${failedResponses.length} players`);
      }
      
      return Promise.all(responses.map(r => r.json()));
    },
    onSuccess: (_, { playerIds }) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/players/'] });
      queryClient.invalidateQueries({ queryKey: ['/api/players/deleted'] });
      
      // Update the players list cache to remove the deleted players
      queryClient.setQueryData(['/api/players/'], (oldData: Player[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter(player => !playerIds.includes(player.id));
      });
      
      toast({
        title: 'Players removed',
        description: `${playerIds.length} players have been removed from your database.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove players',
        variant: 'destructive',
      });
    },
  });

  // Restore player mutation
  const restorePlayerMutation = useMutation({
    mutationFn: async (playerId: number) => {
      const response = await apiRequest('POST', `/api/players/${playerId}/restore`);
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Failed to restore player');
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/players/'] });
      queryClient.invalidateQueries({ queryKey: ['/api/players/deleted'] });
      
      toast({
        title: 'Player restored',
        description: 'The player has been restored to your database.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to restore player',
        variant: 'destructive',
      });
    },
  });

  return {
    deletedPlayers,
    isLoadingDeleted,
    removePlayer: removePlayerMutation.mutate,
    removeManyPlayers: removeManyPlayersMutation.mutate,
    restorePlayer: restorePlayerMutation.mutate,
    isRemoving: removePlayerMutation.isPending || removeManyPlayersMutation.isPending,
    isRestoring: restorePlayerMutation.isPending,
  };
}